import React from 'react';
import { Phone, ActivitySquare, Heart, HeartPulse, Ambulance, Siren, Clock, GraduationCap, Zap, Shield, DollarSign } from 'lucide-react';
import { brandInfo, services, aboutText } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { WobbleCard } from '../components/WobbleCard';

const iconMap = {
  ActivitySquare,
  Heart,
  HeartPulse,
  Ambulance,
  Siren
};

export const Home = () => {
  // All images from the public folder
  const heroImages = [
    '/1.webp',
    '/2.webp',
    '/3.webp',
    '/4.webp',
    '/5.webp',
    '/6.webp',
    '/7.webp',
    '/8.webp',
    '/9.webp',
    '/10.webp',
    '/11.webp',
    '/12.webp',
    '/13.webp',
    '/14.jpeg',
    '/15.jpeg',
    '/16.jpeg',
    '/17.jpeg',
    '/18.jpeg',
    '/19.jpeg',
    '/20.jpeg',
    '/21.jpeg',
    '/22.jpeg',
    '/23.jpeg',
    '/24.jpeg',
    '/25.jpeg'
  ];

  // Split images into distinct rows - no repeats
  const row1Images = heroImages.slice(0, 9); // Images 1-9
  const row2Images = heroImages.slice(9, 17); // Images 10-17
  const row3Images = heroImages.slice(17); // Images 18-25

  // Map services to images
  const serviceImages = {
    1: '/14.jpeg', // Advanced Life Support (ALS)
    2: '/26.jpeg', // Basic Life Support (BLS)
    3: '/19.jpeg', // Human Remains Transportation Services
    4: '/18.jpeg', // Air Ambulance (24/7)
    5: '/15.jpeg'  // Funeral Support Services
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-white py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
            {/* Content on Left */}
            <div className="text-gray-900 z-10 order-2 md:order-1">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
            {brandInfo.tagline}
          </h1>
              <p className="text-lg sm:text-xl md:text-2xl mb-6 md:mb-8 text-gray-600">
            {brandInfo.subtext}
          </p>
              <div className="flex flex-col sm:flex-row  sm:items-start gap-3 sm:gap-4">
            <Button
              asChild
              size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-xl w-full sm:w-auto"
            >
              <a href={`tel:${brandInfo.phones[0]}`}>
                <Phone className="w-5 h-5 mr-2" />
                Enquire Now
              </a>
            </Button>
           
              </div>
            </div>

            {/* Scrolling Images on Right */}
            <div className="w-full h-[400px] sm:h-[450px] md:h-[500px] lg:h-[600px] relative overflow-hidden order-1 md:order-2">
              {/* Left blur gradient */}
              <div className="absolute left-0 top-0 w-12 sm:w-16 md:w-20 h-full bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none"></div>
              {/* Right blur gradient */}
              <div className="absolute right-0 top-0 w-12 sm:w-16 md:w-20 h-full bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none"></div>
              <div className="h-full flex flex-col gap-3 sm:gap-4">
                {/* Row 1 - Scrolls Right */}
                <div className="flex gap-3 sm:gap-4 animate-scroll-right">
                  {row1Images.map((image, index) => (
                    <div key={`row1-${index}`} className="flex-shrink-0 w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 overflow-hidden rounded-lg">
                      <img
                        src={image}
                        alt={`Hero image ${index + 1}`}
                        className="w-full h-full object-cover object-center scale-110 rounded-lg"
                      />
                    </div>
                  ))}
                  {/* Duplicate for seamless loop */}
                  {row1Images.map((image, index) => (
                    <div key={`row1-dup-${index}`} className="flex-shrink-0 w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 overflow-hidden rounded-lg">
                      <img
                        src={image}
                        alt={`Hero image ${index + 1}`}
                        className="w-full h-full object-cover object-center scale-110 rounded-lg"
                      />
                    </div>
                  ))}
                </div>
                
                {/* Row 2 - Scrolls Left */}
                <div className="flex gap-3 sm:gap-4 animate-scroll-left">
                  {row2Images.map((image, index) => (
                    <div key={`row2-${index}`} className="flex-shrink-0 w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 overflow-hidden rounded-lg">
                      <img
                        src={image}
                        alt={`Hero image ${index + 1}`}
                        className="w-full h-full object-cover object-center scale-110 rounded-lg"
                      />
                    </div>
                  ))}
                  {/* Duplicate for seamless loop */}
                  {row2Images.map((image, index) => (
                    <div key={`row2-dup-${index}`} className="flex-shrink-0 w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 overflow-hidden rounded-lg">
                      <img
                        src={image}
                        alt={`Hero image ${index + 1}`}
                        className="w-full h-full object-cover object-center scale-110 rounded-lg"
                      />
                    </div>
                  ))}
                </div>
                
                {/* Row 3 - Scrolls Right */}
                <div className="flex gap-3 sm:gap-4 animate-scroll-right">
                  {row3Images.map((image, index) => (
                    <div key={`row3-${index}`} className="flex-shrink-0 w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 overflow-hidden rounded-lg">
                      <img
                        src={image}
                        alt={`Hero image ${index + 1}`}
                        className="w-full h-full object-cover object-center scale-110 rounded-lg"
                      />
                    </div>
                  ))}
                  {/* Duplicate for seamless loop */}
                  {row3Images.map((image, index) => (
                    <div key={`row3-dup-${index}`} className="flex-shrink-0 w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 overflow-hidden rounded-lg">
                      <img
                        src={image}
                        alt={`Hero image ${index + 1}`}
                        className="w-full h-full object-cover object-center scale-110 rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Badge className="bg-red-600 text-white text-base md:text-lg px-6 py-2 rounded-full border-none hover:bg-red-700">
                Why Choose Us
              </Badge>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Trusted emergency medical transportation with unmatched quality and reliability.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {/* 24/7 Emergency Availability */}
            <Card className="border-2 hover:border-red-500 transition-all duration-300 hover:shadow-xl bg-white">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <Clock className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">24/7 Emergency Availability</h3>
                <p className="text-gray-600">Always ready for immediate response, day or night.</p>
              </CardContent>
            </Card>

            {/* Advanced Life Support (ALS) Equipped */}
            <Card className="border-2 hover:border-red-500 transition-all duration-300 hover:shadow-xl bg-white">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <HeartPulse className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Advanced Life Support (ALS) Equipped</h3>
                <p className="text-gray-600">Ventilator, defibrillator, cardiac monitor, IV support, airway management.</p>
              </CardContent>
            </Card>

            {/* Trained & Certified Paramedics */}
            <Card className="border-2 hover:border-red-500 transition-all duration-300 hover:shadow-xl bg-white">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <GraduationCap className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Trained & Certified Paramedics</h3>
                <p className="text-gray-600">Experienced medical staff trained for emergencies and trauma care.</p>
              </CardContent>
            </Card>

            {/* Fast Response Time */}
            <Card className="border-2 hover:border-red-500 transition-all duration-300 hover:shadow-xl bg-white">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <Zap className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Response Time</h3>
                <p className="text-gray-600">Quick arrival for critical situations.</p>
              </CardContent>
            </Card>

            {/* Clean & Fully Sanitized Ambulances */}
            <Card className="border-2 hover:border-red-500 transition-all duration-300 hover:shadow-xl bg-white">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Clean & Fully Sanitized Ambulances</h3>
                <p className="text-gray-600">Regularly maintained and infection-free interiors.</p>
              </CardContent>
            </Card>

            {/* Affordable Emergency Transport */}
            <Card className="border-2 hover:border-red-500 transition-all duration-300 hover:shadow-xl bg-white">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <DollarSign className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Affordable Emergency Transport</h3>
                <p className="text-gray-600">Transparent pricing with no hidden charges.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Badge className="bg-red-600 text-white text-base md:text-lg px-6 py-2 rounded-full border-none hover:bg-red-700">
                Our Services
              </Badge>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive emergency medical services equipped with advanced life support equipment and trained professionals.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const serviceImage = serviceImages[service.id];
              return (
                <WobbleCard
                  key={service.id}
                  image={serviceImage}
                  title={service.title}
                  description={service.description}
                  features={service.features || []}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-center mb-12">
            <Badge className="bg-red-600 text-white text-base md:text-lg px-6 py-2 rounded-full border-none hover:bg-red-700">
              About Us
            </Badge>
          </div>
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Image on Left */}
            <div className="w-full h-full">
              <img 
                src="/7.webp" 
                alt="About Us" 
                className="w-full h-auto rounded-lg shadow-lg object-cover"
              />
            </div>
            {/* Content on Right */}
            <div>
              <p className="text-gray-700 text-lg leading-relaxed">
                {aboutText}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Badge className="bg-red-600 text-white text-base md:text-lg px-6 py-2 rounded-full border-none hover:bg-red-700">
                Testimonials
              </Badge>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg mb-4">
              What our clients say about our service
            </p>
            <div className="flex justify-center items-center gap-2">
              <Badge className="bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2 px-4 py-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.99 7.28-2.69l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-sm font-semibold">5 Star in Google Reviews</span>
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Testimonial 1 */}
            <Card className="border-2 hover:border-red-500 transition-all duration-300 hover:shadow-xl bg-gray-50">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Good service
                </p>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Staff very friendly and helping mentality
                </p>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Sanitized vehicle
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Thanks for giving good service may god bless you
                </p>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-gray-900 font-semibold">Preethy Prasanth</p>
                  <p className="text-gray-500 text-sm mt-1">5 months ago</p>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 */}
            <Card className="border-2 hover:border-red-500 transition-all duration-300 hover:shadow-xl bg-gray-50">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Best and good behaviour of staff. They helped us so much.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Payment was reasonable. Keep it up 👍🙏
                </p>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-gray-900 font-semibold">Shafeeq P</p>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 */}
            <Card className="border-2 hover:border-red-500 transition-all duration-300 hover:shadow-xl bg-gray-50">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Excellent service timely reached and pickup and shift was very smooth well behaviour and helping mentality vehicle is neat and clean thank you for giving good service
                </p>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-gray-900 font-semibold">Monjith Mohandas</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-center mb-8">
            <Badge className="bg-red-600 text-white text-base md:text-lg px-6 py-2 rounded-full border-none hover:bg-red-700">
              Find Us
            </Badge>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <iframe
                src={brandInfo.mapEmbed}
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Care Ambulance Service Location"
              ></iframe>
            </div>
            <div className="mt-6 text-center">
              <Button asChild variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                <a href={brandInfo.mapLink} target="_blank" rel="noopener noreferrer">
                  Open in Google Maps
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};